import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import './ro.css';
import './mobile-fixes.css';
import './typography.css';
import './mobile-review-preview.css';
import './qa-fixes.css';
import './round1.css';
import PauseDialog from './components/PauseDialog';
import './components/materials.css';
import Topbar from './components/Topbar';
import Sidebar from './components/Sidebar';
import PrototypeControls from './components/PrototypeControls';
import WelcomeFlow from './components/WelcomeFlow';
import { amyLogo } from './brand/logos';
import { SUPPORTED_LOCALES } from './i18n/locales';
import ChatPanel from './components/ChatPanel';
import CalendarPage from './components/CalendarPage';
import type { CalendarView } from './components/CalendarPage';
import PostView from './components/PostView';
import MaterialBank from './components/MaterialBank';
import BrandPage from './components/BrandPage';
import AllowancePage from './components/AllowancePage';
import SettingsPage from './components/SettingsPage';
import OnboardingPage from './components/OnboardingPage';
import IdeasPage from './components/IdeasPage';
import ReportsPage from './components/ReportsPage';
import AgencyHome from './components/AgencyHome';
import PlatformHome from './components/PlatformHome';
import NotYetSheet from './components/NotYetSheet';
import { dictionaries, type Language } from './i18n/locales';
import { ApplicationContext, createTranslator } from './i18n/AppContext';
import type { Page, Role } from './types';
import SafeIcon from './components/SafeIcon';
import { useDemo, useWorkspaceData } from './store/DemoStore';
import type { Actor } from './store/DemoStore';
import { useHashRoute } from './store/route';
import { UiContext } from './store/ui';
import { DemoStoreProvider } from './store/DemoStoreProvider';

export default function AppRoot() {
  return (
    <DemoStoreProvider>
      <Shell />
    </DemoStoreProvider>
  );
}

const LANGUAGE_STORAGE_KEY = 'amy-language';
const PHONE_PREVIEW_HEIGHT = 844;
const PHONE_PREVIEW_VERTICAL_MARGIN = 36;
const DOCKED_CHAT_MINIMUM_VIEWPORT_WIDTH = 1440;
const TOAST_DURATION_IN_MILLISECONDS = 3200;

function initialRoleForPage(page: Page): Role {
  if (page === 'agencyHome') return 'agency';
  if (page === 'platformHome') return 'admin';
  return 'owner';
}

function readSavedLanguage(): Language {
  try {
    const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return savedLanguage && savedLanguage in dictionaries ? (savedLanguage as Language) : 'en';
  } catch {
    return 'en';
  }
}

function rememberLanguageIfStorageIsAvailable(language: Language): boolean {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    return true;
  } catch {
    return false;
  }
}

function shellLevelForPage(page: Page) {
  if (page === 'agencyHome') return 'Agency';
  if (page === 'platformHome') return 'Platform';
  return 'Workspace';
}

function actorForRole(role: Role, workspace: { contributor: string; owner: string }): Actor {
  if (role === 'contributor') return { name: workspace.contributor, role };
  if (role === 'agency') return { name: 'Hakusana', role };
  if (role === 'admin') return { name: 'Platform admin', role };
  return { name: workspace.owner, role: 'owner' };
}

const isPostScreen = (page: Page) => page === 'review' || page === 'post';
const isOrganisationHome = (page: Page) => page === 'agencyHome' || page === 'platformHome';

const byDayThenTime = (
  first: { day: number; time: string },
  second: { day: number; time: string }
) => first.day - second.day || first.time.localeCompare(second.time);

function Shell() {
  const { route, navigate } = useHashRoute();
  const { commands } = useDemo();
  const workspaceData = useWorkspaceData(route.workspaceId);
  const [role, setRole] = useState<Role>(() => initialRoleForPage(route.page));
  const [viewingClient, setViewingClient] = useState(false);
  const [language, setLanguageState] = useState<Language>(readSavedLanguage);
  const setLanguage = (nextLanguage: Language) => {
    setLanguageState(nextLanguage);
    rememberLanguageIfStorageIsAvailable(nextLanguage);
  };
  const [mobile, setMobile] = useState(false);
  const [phoneScale, setPhoneScale] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatPrefill, setChatPrefill] = useState('');
  const [chatThread, setChatThread] = useState<{ id: string; openRequestNumber: number } | null>(
    null
  );
  const [resumeOpen, setResumeOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [toastTone, setToastTone] = useState<'ok' | 'warn'>('ok');
  const [notYetFeatureTitle, setNotYetFeatureTitle] = useState<string | null>(null);
  const [reviewQueue, setReviewQueue] = useState<{ workspaceId: string; ids: string[] }>({
    workspaceId: '',
    ids: [],
  });
  const [calendar, setCalendar] = useState<{
    view: CalendarView;
    cursor: number;
    channels: string[] | null;
  }>({ view: 'week', cursor: 16, channels: null });

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  useEffect(() => {
    const updateViewportSize = () => {
      const availablePhoneHeight = window.innerHeight - PHONE_PREVIEW_VERTICAL_MARGIN;
      setPhoneScale(Math.min(1, availablePhoneHeight / PHONE_PREVIEW_HEIGHT));
      setViewportWidth(window.innerWidth);
    };
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    return () => window.removeEventListener('resize', updateViewportSize);
  }, []);

  const translate = createTranslator(language);
  const labels = dictionaries[language];
  const page = route.page;
  const { workspace, settings } = workspaceData;

  useEffect(() => {
    const isClientRole = role === 'owner' || role === 'contributor';
    const clientLandedOnOrganisationHome = isClientRole && isOrganisationHome(page);
    if (clientLandedOnOrganisationHome) navigate({ page: 'calendar' }, { replace: true });
    const adminLeftPlatformHome = role === 'admin' && page !== 'platformHome';
    if (adminLeftPlatformHome) navigate({ page: 'platformHome' }, { replace: true });
  }, [role, page, navigate]);

  const effectiveRole: Role = viewingClient ? 'owner' : role;
  const agencyInWorkspace = role === 'agency' && !viewingClient && page !== 'agencyHome';
  const clientSide =
    effectiveRole === 'owner' || effectiveRole === 'contributor' || agencyInWorkspace;
  const shellLevel = shellLevelForPage(page);
  const actor = actorForRole(role, workspace);
  const canApprove =
    !viewingClient &&
    (role === 'owner' || (role === 'contributor' && settings.contributorCanApprove));
  const canStop =
    !viewingClient &&
    (role === 'owner' ||
      role === 'agency' ||
      (role === 'contributor' && settings.contributorCanStop));
  const chatCanDock =
    !mobile && viewportWidth >= DOCKED_CHAT_MINIMUM_VIEWPORT_WIDTH && !isPostScreen(page);
  const chatDocked = clientSide && chatCanDock && !chatCollapsed;

  function notify(message: string, tone?: 'warn') {
    setToastTone(tone ?? 'ok');
    setToast(message);
    window.setTimeout(() => setToast(''), TOAST_DURATION_IN_MILLISECONDS);
  }

  function openChat(prefill?: string, conversationId?: string) {
    setChatPrefill(prefill ?? '');
    if (conversationId)
      setChatThread((currentThread) => ({
        id: conversationId,
        openRequestNumber: (currentThread?.openRequestNumber ?? 0) + 1,
      }));
    if (chatCanDock) setChatCollapsed(false);
    else setChatOpen(true);
  }
  function closeChat() {
    if (chatDocked) setChatCollapsed(true);
    else setChatOpen(false);
  }

  const needsReview = (postId: string) =>
    workspaceData.posts.find((post) => post.id === postId)?.status === 'Needs your review';

  const reviewSessionPostIds = useMemo(() => {
    if (page !== 'review') return [];
    const routeIsInsideSavedQueue =
      !route.id || route.id === 'done' || reviewQueue.ids.includes(route.id);
    if (reviewQueue.workspaceId === route.workspaceId && routeIsInsideSavedQueue)
      return reviewQueue.ids;
    const postIdsNeedingReview = workspaceData.posts
      .filter((post) => post.status === 'Needs your review')
      .sort(byDayThenTime)
      .map((post) => post.id);
    const routePostId = route.id;
    const routePostMissingFromQueue =
      routePostId && routePostId !== 'done' && !postIdsNeedingReview.includes(routePostId);
    if (routePostMissingFromQueue) postIdsNeedingReview.unshift(routePostId);
    return postIdsNeedingReview;
  }, [page, reviewQueue, route.workspaceId, route.id, workspaceData.posts]);
  useEffect(() => {
    const savedQueueIsStale =
      reviewQueue.workspaceId !== route.workspaceId || reviewQueue.ids !== reviewSessionPostIds;
    if (page === 'review' && savedQueueIsStale)
      setReviewQueue({ workspaceId: route.workspaceId, ids: reviewSessionPostIds });
  }, [page, reviewSessionPostIds, reviewQueue, route.workspaceId]);

  function startReview(postId?: string, onlyTheseCampaignPostIds?: string[]) {
    const queuedPostIds = workspaceData.posts
      .filter(
        (post) =>
          post.status === 'Needs your review' &&
          (!onlyTheseCampaignPostIds || onlyTheseCampaignPostIds.includes(post.id))
      )
      .sort(byDayThenTime)
      .map((post) => post.id);
    setReviewQueue({ workspaceId: route.workspaceId, ids: queuedPostIds });
    setChatOpen(false);
    navigate({ page: 'review', id: postId ?? queuedPostIds[0] ?? 'done' });
  }

  const unresolvedPostIdsInQueue = reviewSessionPostIds.filter(needsReview);
  function nextInReview(currentPostId: string, skipping = false) {
    const currentIndex = reviewSessionPostIds.indexOf(currentPostId);
    const isAnotherPendingPost = (postId: string) =>
      postId !== currentPostId && needsReview(postId);
    const nextPendingAfterCurrent = reviewSessionPostIds
      .slice(currentIndex + 1)
      .find(isAnotherPendingPost);
    const wrappedBackToSkippedPost = skipping
      ? undefined
      : reviewSessionPostIds.slice(0, currentIndex).find(isAnotherPendingPost);
    const nextPostId = nextPendingAfterCurrent ?? wrappedBackToSkippedPost;
    navigate({ page: 'review', id: nextPostId ?? 'done' }, { replace: true });
  }

  function changeRole(nextRole: Role) {
    setViewingClient(false);
    setRole(nextRole);
    setChatOpen(false);
    const agencyKeepsPostInView =
      nextRole === 'agency' && isPostScreen(page) && route.id && route.id !== 'done';
    if (agencyKeepsPostInView) navigate({ page: 'post', id: route.id }, { replace: true });
    else if (nextRole === 'agency') navigate({ page: 'agencyHome' });
    else if (nextRole === 'admin') navigate({ page: 'platformHome' });
    else if (isOrganisationHome(page)) navigate({ page: 'calendar' });
    else stayOnSamePageAsAnotherClientRole();
  }

  function stayOnSamePageAsAnotherClientRole() {
    navigate({ page, id: route.id }, { replace: true });
  }

  function navigatePage(nextPage: Page) {
    setChatOpen(false);
    setSidebarOpen(false);
    navigate({ page: nextPage });
  }

  function renderPage() {
    if (page === 'agencyHome' && role === 'agency' && !viewingClient) {
      return (
        <AgencyHome
          onOpenWorkspace={(workspaceId) =>
            navigate({ workspaceId: workspaceId as never, page: 'calendar' })
          }
          onViewClient={(workspaceId) => {
            setViewingClient(true);
            navigate({ workspaceId: workspaceId as never, page: 'calendar' });
            notify(translate('Client view opened in read-only mode'));
          }}
          onOpenReview={(workspaceId) => {
            navigate({ workspaceId: workspaceId as never, page: 'calendar' });
          }}
        />
      );
    }
    if (page === 'platformHome') return <PlatformHome />;
    if (isPostScreen(page)) {
      const reviewing = page === 'review';
      const postId = reviewing && !route.id ? (reviewSessionPostIds[0] ?? 'done') : route.id;
      const post = workspaceData.posts.find((candidate) => candidate.id === postId);
      const isVersionOfSamePost = (candidate: (typeof workspaceData.posts)[number]) =>
        post?.groupId
          ? candidate.groupId === post.groupId
          : candidate.title === post?.title && candidate.day === post?.day;
      const channelVersions = post
        ? workspaceData.posts.filter(
            (candidate) =>
              isVersionOfSamePost(candidate) &&
              !['Rejected', 'Cancelled'].includes(candidate.status)
          )
        : [];
      return (
        <PostView
          key={`${page}-${postId}`}
          post={reviewing && postId === 'done' ? undefined : post}
          reviewing={reviewing}
          index={reviewing && post ? reviewSessionPostIds.indexOf(post.id) : 0}
          total={reviewSessionPostIds.length}
          siblings={channelVersions}
          onOpen={(openedPostId) =>
            navigate({ page: reviewing ? 'review' : 'post', id: openedPostId }, { replace: true })
          }
          onPrevious={() => {
            const currentIndex = post ? reviewSessionPostIds.indexOf(post.id) : -1;
            if (currentIndex > 0)
              navigate(
                { page: 'review', id: reviewSessionPostIds[currentIndex - 1] },
                { replace: true }
              );
          }}
          onNext={() => post && nextInReview(post.id, true)}
          waiting={unresolvedPostIdsInQueue.length}
          skipped={unresolvedPostIdsInQueue}
          onClose={() => navigate({ page: 'calendar' })}
          onDecided={(decidedPostId) => reviewing && nextInReview(decidedPostId)}
          labels={labels}
        />
      );
    }
    if (page === 'materials') return <MaterialBank />;
    if (page === 'brand') return <BrandPage />;
    if (page === 'allowance') return <AllowancePage />;
    if (page === 'settings') return <SettingsPage />;
    if (page === 'onboarding')
      return <OnboardingPage onBack={() => navigate({ page: 'calendar' })} />;
    if (page === 'welcome') return <WelcomeFlow />;
    if (page === 'ideas') return <IdeasPage />;
    if (page === 'reports') return <ReportsPage />;
    return (
      <CalendarPage
        state={calendar}
        setState={setCalendar}
        labels={labels}
        onReview={() => startReview()}
        onPost={(postId) => navigate({ page: 'post', id: postId })}
        onSetup={() => navigate({ page: 'onboarding' })}
      />
    );
  }

  const appClassNames = [
    mobile ? 'mobile-preview' : 'amy-app',
    clientSide && !mobile && !chatDocked ? ' chat-floating' : '',
    isPostScreen(page) ? ' review-screen-open' : '',
    page === 'onboarding' ? ' onboarding-screen-open' : '',
  ].join('');
  const uiContextValue = {
    route,
    navigate,
    role: effectiveRole,
    actor,
    notify,
    showPlannedFeatureNotice: (title: string) => setNotYetFeatureTitle(title),
    openChat,
    canApprove,
    canStop,
    startReview,
  };
  const showClientTools = clientSide && !isOrganisationHome(page);

  return (
    <ApplicationContext.Provider value={{ language, readOnly: viewingClient, setLanguage }}>
      <UiContext.Provider value={uiContextValue}>
        <div
          className={mobile ? 'mobile-preview-stage' : 'desktop-stage'}
          style={mobile ? ({ '--phone-preview-zoom': phoneScale } as CSSProperties) : undefined}
        >
          {page === 'welcome' ? (
            <div className={`${appClassNames} welcome-screen`}>
              <header className="welcome-header">
                <img className="brand-logo" src={amyLogo} alt="AMY" width={114} height={32} />
                <select
                  className="select-field"
                  aria-label={translate('Language')}
                  value={language}
                  onChange={(event) => setLanguage(event.target.value as Language)}
                >
                  {SUPPORTED_LOCALES.map((locale) => (
                    <option key={locale.code} value={locale.code}>
                      {locale.flag} · {locale.name}
                    </option>
                  ))}
                </select>
              </header>
              <main className="welcome-main">{renderPage()}</main>
            </div>
          ) : (
            <div className={appClassNames}>
              <Topbar
                language={language}
                setLanguage={setLanguage}
                level={shellLevel}
                showWorkspace={showClientTools}
                onMenu={() => setSidebarOpen(true)}
                onReset={() => {
                  commands.reset();
                  notify(translate('Demo data reset'));
                }}
              />
              {viewingClient && (
                <div className="notice-banner app-banner">
                  <strong>
                    <SafeIcon name="FiEye" />{' '}
                    {translate('Viewing {ws} as the client · read-only', { ws: workspace.name })}
                  </strong>
                  <button
                    className="button small"
                    onClick={() => {
                      setViewingClient(false);
                      navigate({ page: 'agencyHome' });
                    }}
                  >
                    {translate('Exit')}
                  </button>
                </div>
              )}
              {agencyInWorkspace && (
                <div className="notice-banner app-banner agency-banner">
                  <strong>
                    <SafeIcon name="FiBriefcase" />{' '}
                    {translate('Hakusana is working in {ws}. The client approves posts.', {
                      ws: workspace.name,
                    })}
                  </strong>
                  <button className="button small" onClick={() => navigate({ page: 'agencyHome' })}>
                    <SafeIcon name="FiArrowLeft" />
                    {translate('Agency home')}
                  </button>
                </div>
              )}
              {showClientTools && settings.paused && (
                <div className="notice-banner app-banner paused-banner" role="status">
                  <strong>
                    <SafeIcon name="FiPauseCircle" />{' '}
                    {translate(
                      'Publishing is paused by {name}. Nothing is published until it is resumed.',
                      { name: settings.pausedBy ?? workspace.owner }
                    )}
                  </strong>
                  {effectiveRole === 'owner' && !viewingClient && (
                    <button className="button small" onClick={() => setResumeOpen(true)}>
                      <SafeIcon name="FiPlayCircle" />
                      {translate('Resume publishing')}
                    </button>
                  )}
                </div>
              )}
              <div
                className="app-layout"
                style={
                  showClientTools && chatDocked
                    ? undefined
                    : { gridTemplateColumns: '220px minmax(0,1fr)' }
                }
              >
                <Sidebar
                  page={page}
                  setPage={navigatePage}
                  role={agencyInWorkspace ? 'owner' : effectiveRole}
                  open={sidebarOpen}
                  labels={labels}
                  onClose={() => setSidebarOpen(false)}
                  language={language}
                  setLanguage={setLanguage}
                />
                <main className="main-area">{renderPage()}</main>
                {showClientTools && (
                  <ChatPanel
                    open={chatOpen}
                    onClose={closeChat}
                    prefill={chatPrefill}
                    thread={chatThread}
                    reviewPostId={isPostScreen(page) ? route.id : undefined}
                    onOpenPost={(postId) => {
                      setChatOpen(false);
                      navigate({ page: 'post', id: postId });
                    }}
                  />
                )}
              </div>
              {sidebarOpen && (
                <button
                  className="sidebar-backdrop"
                  aria-label={translate('Close menu')}
                  onClick={() => setSidebarOpen(false)}
                />
              )}
              {showClientTools && !chatDocked && !chatOpen && (
                <button className="button primary mobile-chat-button" onClick={() => openChat()}>
                  <SafeIcon name="FiMessageCircle" />
                  {labels.chat}
                </button>
              )}
            </div>
          )}
          <PrototypeControls
            role={role}
            setRole={changeRole}
            mobile={mobile}
            setMobile={setMobile}
            labels={labels}
            onReset={() => {
              commands.reset();
              setViewingClient(false);
              notify(translate('Demo data reset'));
            }}
            onNewClient={() => {
              commands.resetNewClient();
              changeRole('owner');
              setViewingClient(false);
              navigate({ workspaceId: 'nowa', page: 'welcome' });
            }}
          />
          {notYetFeatureTitle && (
            <NotYetSheet title={notYetFeatureTitle} onClose={() => setNotYetFeatureTitle(null)} />
          )}
          {resumeOpen && <PauseDialog mode="resume" onClose={() => setResumeOpen(false)} />}
          {toast && (
            <div className={`toast ${toastTone === 'warn' ? 'toast-warn' : ''}`} role="status">
              <SafeIcon name={toastTone === 'warn' ? 'FiAlertTriangle' : 'FiCheckCircle'} /> {toast}
            </div>
          )}
        </div>
      </UiContext.Provider>
    </ApplicationContext.Provider>
  );
}
